import { useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { getStoredCurrentEnvironment } from "@/lib/environment";
import ForexOperationsOverviewContainer from "./components/operations-overview/ForexOperationsOverviewContainer";
import AgentListTab from "./components/AgentListTab";
import SubAccountAuditTab from "./components/SubAccountAuditTab";
import FollowMonitorTab from "./components/FollowMonitorTab";
import AccountCommandTab from "./components/AccountCommandTab";
import ConfigCenterTab from "./components/ConfigCenterTab";
import BusinessClosedLoopTab from "./components/BusinessClosedLoopTab";
import PerformanceTab from "./components/PerformanceTab";
import ModelUsageTab from "./components/ModelUsageTab";
import AnomaliesTab from "./components/AnomaliesTab";
import CreateAgentTab from "./components/CreateAgentTab";
import AgentReviewTab from "./components/AgentReviewTab";
import AgentModelConfigTab from "./components/AgentModelConfigTab";
import MasterBindTab from "./components/MasterBindTab";
import TerminalAnomaliesTab from "./components/TerminalAnomaliesTab";
import SubDetailTab from "./components/SubDetailTab";
import FollowDetailTab from "./components/FollowDetailTab";
import ExecutionDetailTab from "./components/ExecutionDetailTab";
import AccountExecutionIssuesTab from "./components/AccountExecutionIssuesTab";
import KillSwitchTab from "./components/KillSwitchTab";
import RiskConfigSummaryTab from "./components/RiskConfigSummaryTab";
import ComplianceTab from "./components/ComplianceTab";
import QueryPolicyTab from "./components/QueryPolicyTab";
import AdminUsersTab from "./components/AdminUsersTab";
import RolesTab from "./components/RolesTab";
import AuditTab from "./components/AuditTab";
import SecurityTab from "./components/SecurityTab";
import ModelLogsTab from "./components/ModelLogsTab";

/* ---- section from URL ---- */
function sectionFromPath(pathname: string): string {
  /* --- P0 全屏独立页面 --- */
  if (pathname.startsWith("/forex/master-bind")) return "master-bind";
  if (pathname.startsWith("/forex/terminal-anomaly")) return "terminal-anomalies";
  if (pathname.startsWith("/forex/model-config")) return "agents-model-config";
  /* --- explicit detail/child paths first (safety net even though these go to dedicated page components) --- */
  if (pathname.startsWith("/forex/agents/review/detail")) return "agents-review";
  if (pathname.startsWith("/forex/agents/detail")) return "agents";
  if (pathname.startsWith("/forex/master/detail")) return "master";
  if (pathname.startsWith("/forex/master/bind")) return "master-bind";
  if (pathname.startsWith("/forex/master/add")) return "master-bind";
  if (pathname.startsWith("/forex/terminal/anomalies/detail")) return "terminal-anomalies";
  if (pathname.startsWith("/forex/terminal/detail")) return "terminal";
  if (pathname.startsWith("/forex/terminal/create")) return "terminal";
  if (pathname.startsWith("/forex/sub-audit/detail")) return "sub-audit";
  if (pathname.startsWith("/forex/sub-detail/bind")) return "sub-detail";
  if (pathname.startsWith("/forex/sub-detail/detail")) return "sub-detail";
  if (pathname.startsWith("/forex/follow/detail")) return "follow-detail";
  if (pathname.startsWith("/forex/execution-detail/detail")) return "execution-detail";
  if (pathname.startsWith("/forex/model-logs/detail")) return "model-logs";
  if (pathname.startsWith("/forex/risk-config/detail")) return "risk-config";
  if (pathname.startsWith("/forex/kill-switch/detail")) return "kill-switch";
  if (pathname.startsWith("/forex/query-policy/detail")) return "query-policy";
  /* --- P2 全屏页面 --- */
  if (pathname.startsWith("/forex/symbol-config/detail")) return "config";
  if (pathname.startsWith("/forex/config/releases/detail")) return "audit";
  if (pathname.startsWith("/forex/config/releases")) return "audit";
  /* --- broad matches (keep existing order but remove detail sub-path collisions) --- */
  if (pathname.includes("/forex/performance")) return "performance";
  if (pathname.includes("/forex/model-usage")) return "model-usage";
  if (pathname.includes("/forex/anomalies")) return "anomalies";
  if (pathname.includes("/forex/agents/create")) return "agents-create";
  if (pathname.includes("/forex/agents/review")) return "agents-review";
  if (pathname.includes("/forex/agents/model-config")) return "agents-model-config";
  if (pathname === "/forex/agents" || pathname.endsWith("/forex/agents")) return "agents";
  if (pathname.includes("/forex/master")) return "master";
  if (pathname.includes("/forex/terminal/anomalies")) return "terminal-anomalies";
  if (pathname.includes("/forex/terminal")) return "terminal";
  if (pathname.includes("/forex/sub-detail")) return "sub-detail";
  if (pathname.includes("/forex/sub-audit")) return "sub-audit";
  if (pathname.includes("/forex/follow-detail")) return "follow";
  if (pathname.includes("/forex/follow")) return "follow";
  if (pathname.includes("/forex/execution-detail")) return "execution-detail";
  if (pathname.includes("/forex/account-execution-issues")) return "account-execution-issues";
  if (pathname.includes("/forex/account-commands")) return "account-commands";
  if (pathname.includes("/forex/kill-switch")) return "kill-switch";
  if (pathname.includes("/forex/risk-config")) return "risk-config";
  if (pathname.includes("/forex/compliance")) return "compliance";
  if (pathname.includes("/forex/query-policy")) return "query-policy";
  if (pathname.includes("/forex/config")) return "config";
  if (pathname.includes("/forex/bc-loop")) return "bc-loop";
  if (pathname.includes("/forex/admin-users")) return "admin-users";
  if (pathname.includes("/forex/roles")) return "roles";
  if (pathname.includes("/forex/audit")) return "audit";
  if (pathname.includes("/forex/security")) return "security";
  if (pathname.includes("/forex/model-logs")) return "model-logs";
  return "overview";
}

/* ============================ 主页面 ============================ */
export default function ForexPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const activeSection = sectionFromPath(location.pathname);
  const [environment, setEnvironment] = useState(getStoredCurrentEnvironment);
  useEffect(() => {
    const changed = () => setEnvironment(getStoredCurrentEnvironment());
    window.addEventListener("admin-environment-changed", changed);
    return () => window.removeEventListener("admin-environment-changed", changed);
  }, []);
  const environmentLabel = environment?.display_name || "环境未确认";
  const environmentKey = environment?.id || environment?.env_code || "unselected";
  const overviewTargets: Record<string, string> = {
    agents: "/forex/agents", "agent-reviews": "/forex/agents/audit",
    "follower-reviews": "/forex/sub-accounts", "account-commands": "/forex/account-commands",
    exceptions: "/forex/exceptions",
    terminals: "/forex/terminal", accounts: "/forex/sub-accounts",
    profit: "/forex/performance", models: "/forex/model-usage",
  };

  return (
    <div className="min-h-[500px]">
      {activeSection === "overview" && <ForexOperationsOverviewContainer key={environmentKey} environmentLabel={environmentLabel} onNavigate={(target) => {
        if (target.startsWith("agent:")) {
          navigate(`/forex/agents/detail?id=${encodeURIComponent(target.slice(6))}`);
          return;
        }
        const path = overviewTargets[target];
        if (path) navigate(path);
      }} />}
      {activeSection === "performance" && <PerformanceTab />}
      {activeSection === "model-usage" && <ModelUsageTab />}
      {activeSection === "anomalies" && <AnomaliesTab />}
      {activeSection === "agents" && <AgentListTab />}
      {activeSection === "agents-create" && <CreateAgentTab />}
      {activeSection === "agents-review" && <AgentReviewTab />}
      {activeSection === "agents-model-config" && <AgentModelConfigTab />}
      {activeSection === "master" && <Navigate to="/forex/master-accounts" replace />}
      {activeSection === "master-bind" && <MasterBindTab />}
      {activeSection === "terminal-anomalies" && <TerminalAnomaliesTab />}
      {activeSection === "sub-audit" && <SubAccountAuditTab />}
      {activeSection === "sub-detail" && <SubDetailTab />}
      {activeSection === "follow" && <FollowMonitorTab />}
      {activeSection === "follow-detail" && <FollowDetailTab />}
      {activeSection === "account-commands" && <AccountCommandTab />}
      {activeSection === "execution-detail" && <ExecutionDetailTab />}
      {activeSection === "account-execution-issues" && <AccountExecutionIssuesTab />}
      {activeSection === "risk-config" && <RiskConfigSummaryTab />}
      {activeSection === "kill-switch" && <KillSwitchTab />}
      {activeSection === "config" && <ConfigCenterTab />}
      {activeSection === "compliance" && <ComplianceTab />}
      {activeSection === "query-policy" && <QueryPolicyTab />}
      {activeSection === "bc-loop" && <BusinessClosedLoopTab />}
      {activeSection === "admin-users" && <AdminUsersTab />}
      {activeSection === "roles" && <RolesTab />}
      {activeSection === "audit" && <AuditTab />}
      {activeSection === "security" && <SecurityTab />}
      {activeSection === "model-logs" && <ModelLogsTab />}
    </div>
  );
}
